import type {
  ActionResponse,
  ActionPayload,
  AiChatPayload,
  AiActionItem,
  AiActionExecuteResponse,
  AiActionPlanPayload,
  AiInsightsPayload,
  AiSearchPayload,
  AuthSessionPayload,
  Client360Payload,
  CsrfTokenPayload,
  DashboardPayload,
  DataQualityPayload,
  EntityDetailPayload,
  FinanceOpsPayload,
  HealthPayload,
  ClientIdentityConnector,
  ClientIdentityOptionsPayload,
  CreateClientHubspotCompanyRequest,
  CreateClientHubspotCompanyResponse,
  ConfirmIdentityLinkRequest,
  ConfirmIdentityLinkResponse,
  ConfirmSuggestedIdentityLinksPayload,
  IdentityLinkCandidatesPayload,
  IdentityLinksPayload,
  ReconcileIdentityLinksPayload,
  AbandonPendingIdentityLinkPayload,
  UnlinkIdentityLinkRequest,
  UnlinkIdentityLinkResponse,
  IntegrationSyncResponse,
  IntegrationTestResponse,
  IntegrationsPayload,
  InternalDayCostSavePayload,
  InternalDayCostSource,
  MarkAllReadResponse,
  NotificationsPayload,
  ReferenceUploadResponse,
  QontoBalancePayload,
  ReconciliationPayload,
  SubcontractingCandidateRequest,
  SubcontractingCandidatesPayload,
  TabDataPayload,
  TabName,
  UserContext,
  VersionPayload,
  WeeklyBriefPayload,
} from './types';
import { applySessionAllocationOverlays } from './sessionAllocationOverlay';
import { applyEntityMutationOverlays } from './entityMutationOverlay';

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL?.trim() || '';
const LOGIN_PATHS = ['/login', '/forgot-password', '/reset-password', '/change-password'];

export interface MyWeekTarget {
  route: string;
  view: string;
  filters?: Record<string, string>;
  year?: number;
}

export interface MyWeekItem {
  key: string;
  title: string;
  subtitle?: string;
  meta?: string;
  tone?: 'neutral' | 'success' | 'warning' | 'danger' | string;
  target?: MyWeekTarget;
  external_url?: string;
  amount?: number;
  amount_label?: string;
  date?: string;
  date_label?: string;
  /** Sessions: who does it (read-only), for the intervenant's photo. */
  resource_name?: string;
  resource_id?: string;
  /**
   * The item's client, raw (read-only, 30/09/2026): '' when the deal or the
   * invoice has no client — `subtitle` may then read « client non lié », a
   * sentence that never gets a logo. Absent from an older server.
   */
  client_name?: string;
  /** Same-origin logo route of that client, given by the server (never built here). */
  client_logo_url?: string;
}

export interface MyWeekSection {
  key: string;
  title: string;
  subtitle?: string;
  count: number;
  items: MyWeekItem[];
  empty: string;
}

export interface MyWeekPayload {
  ok: boolean;
  generated_at: string;
  selected_year: number;
  week: {
    start: string;
    end: string;
    label: string;
    month_key: string;
  };
  metrics: Record<string, number | boolean | null>;
  warnings: string[];
  sources?: Record<string, Record<string, unknown>>;
  sections: MyWeekSection[];
}

export interface RevenueReconciliationIssue {
  key: string;
  severity: 'high' | 'medium' | 'low' | string;
  label: string;
  detail: string;
  amount?: number;
  target?: MyWeekTarget;
}

export interface RevenueReconciliationSummary {
  source_of_truth: string;
  dashboard_ca_reconnu: number | null;
  factures_ca_reconnu: number | null;
  factures_montant_ht: number | null;
  clients_ca_reconnu: number | null;
  clients_montant_ht: number | null;
  factures_count: number | null;
  clients_count: number;
  explicit_invoice_count: number;
  inferred_invoice_count: number;
  linked_invoice_count: number;
  unlinked_invoice_count: number | null;
  explicit_ca_reconnu: number | null;
  explicit_montant_ht: number | null;
  inferred_ca_reconnu: number | null;
  inferred_montant_ht: number | null;
  linked_ca_reconnu: number | null;
  linked_montant_ht: number | null;
  unlinked_ca_reconnu: number | null;
  unlinked_montant_ht: number | null;
  delta_dashboard_vs_factures_reconnu: number | null;
  delta_factures_vs_clients_reconnu: number | null;
  delta_factures_vs_clients_ht: number | null;
  attribution_coverage_pct: number | null;
  persisted_client_coverage_pct: number | null;
}

export interface RevenueReconciliationPayload {
  ok: boolean;
  selected_year: number;
  status: 'aligned' | 'mismatch' | string;
  label: string;
  headline: string;
  summary: RevenueReconciliationSummary;
  issues: RevenueReconciliationIssue[];
}

// CSRF token cache
let _csrfToken: string | null = null;
let _csrfFetchPromise: Promise<string> | null = null;

async function getCsrfToken(): Promise<string> {
  if (_csrfToken) return _csrfToken;
  if (_csrfFetchPromise) return _csrfFetchPromise;
  _csrfFetchPromise = fetch(buildApiUrl('/api/csrf-token'), { credentials: 'include' })
    .then((r) => r.json())
    .then((data: CsrfTokenPayload) => {
      const token = typeof data.csrf_token === 'string' ? data.csrf_token : '';
      _csrfToken = token;
      _csrfFetchPromise = null;
      return token;
    })
    .catch(() => {
      _csrfFetchPromise = null;
      return '';
    });
  return _csrfFetchPromise;
}

export function invalidateCsrfToken(): void {
  _csrfToken = null;
}

/** Interface preferences of the signed-in user (column widths…), kept server-side. */
export async function fetchUserPreferences(): Promise<Record<string, unknown>> {
  const response = await fetch(buildApiUrl('/api/preferences'), { credentials: 'include' });
  const payload = await parseJsonResponse<{ ok: boolean; preferences?: Record<string, unknown> }>(response, 'Load preferences');
  return payload.preferences && typeof payload.preferences === 'object' ? payload.preferences : {};
}

export async function saveUserPreference(key: string, value: unknown): Promise<void> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl(`/api/preferences/${encodeURIComponent(key)}`), {
    method: 'PUT',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify({ value }),
  });
  await parseJsonResponse<{ ok: boolean }>(response, 'Save preference');
}

export function buildApiUrl(path: string): string {
  if (!API_BASE_URL) return path;
  return `${API_BASE_URL.replace(/\/$/, '')}${path}`;
}

function isAuthRedirect(response: Response, bodyText?: string): boolean {
  const target = (() => {
    try {
      return new URL(response.url, window.location.origin).pathname;
    } catch {
      return response.url;
    }
  })();
  if (LOGIN_PATHS.some((path) => target.endsWith(path))) return true;
  const contentType = response.headers.get('content-type') || '';
  if (!contentType.includes('text/html')) return false;
  const html = String(bodyText || '').toLowerCase();
  return html.includes('<form') && (html.includes('password') || html.includes('mot de passe') || html.includes('reset-password'));
}

function redirectToLogin(): never {
  window.location.href = buildApiUrl('/login');
  throw new Error('Session expired. Redirecting to login.');
}

async function parseJsonResponse<T>(response: Response, requestLabel: string): Promise<T> {
  const contentType = response.headers.get('content-type') || '';

  if (response.redirected && isAuthRedirect(response)) {
    return redirectToLogin();
  }

  if (contentType.includes('application/json')) {
    const payload = (await response.json()) as T & { error?: string; ok?: boolean };
    if (!response.ok) {
      throw new Error(payload.error || `${requestLabel} failed: ${response.status}`);
    }
    return payload;
  }

  const text = await response.text();
  if (isAuthRedirect(response, text)) {
    return redirectToLogin();
  }
  if (!response.ok) {
    throw new Error(text || `${requestLabel} failed: ${response.status}`);
  }
  throw new Error(`${requestLabel} returned unexpected content-type: ${contentType || 'unknown'}`);
}

export async function fetchTabData(
  tab: Exclude<TabName, 'dashboard'>,
  year: number,
  options?: { scopeMode?: string; from?: string; to?: string; horizonMonths?: number },
): Promise<TabDataPayload> {
  const params = new URLSearchParams({
    tab,
    year: String(year),
  });
  if (options?.scopeMode) {
    params.set('scope_mode', options.scopeMode);
  }
  if (options?.from) params.set('from', options.from);
  if (options?.to) params.set('to', options.to);
  // Range contract: `to` and `horizon_months` are two alternative ways to
  // bound a request. Never send both (the server rejects ambiguous ranges).
  if (options?.horizonMonths && !options?.to) params.set('horizon_months', String(options.horizonMonths));
  const response = await fetch(buildApiUrl(`/api/data?${params.toString()}`), {
    credentials: 'include',
  });
  const payload = await parseJsonResponse<TabDataPayload>(response, `Load ${tab}`);
  return applyEntityMutationOverlays(applySessionAllocationOverlays(payload)) as TabDataPayload;
}

export async function fetchEntityDetail(
  entityType: 'clients' | 'factures' | 'intervenants' | 'soustraitants',
  entityId: string,
  year: number,
): Promise<EntityDetailPayload> {
  const response = await fetch(
    buildApiUrl(`/api/${encodeURIComponent(entityType)}/${encodeURIComponent(entityId)}?year=${encodeURIComponent(String(year))}`),
    { credentials: 'include' },
  );
  return parseJsonResponse<EntityDetailPayload>(response, `Load ${entityType} detail`);
}

export async function fetchSubcontractingCandidates(
  request: SubcontractingCandidateRequest,
): Promise<SubcontractingCandidatesPayload> {
  const params = new URLSearchParams();
  if (request.missionId?.trim()) params.set('mission_id', request.missionId.trim());
  if (request.start?.trim()) params.set('start', request.start.trim());
  if (request.end?.trim()) params.set('end', request.end.trim());
  if (typeof request.days === 'number' && Number.isFinite(request.days) && request.days > 0) {
    params.set('days', String(request.days));
  }
  if (request.speciality?.trim()) params.set('speciality', request.speciality.trim());
  if (typeof request.pricePerDay === 'number' && Number.isFinite(request.pricePerDay) && request.pricePerDay > 0) {
    params.set('price_per_day', String(request.pricePerDay));
  }
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(buildApiUrl(`/api/subcontracting/candidates${suffix}`), {
    credentials: 'include',
  });
  return parseJsonResponse<SubcontractingCandidatesPayload>(response, 'Load subcontracting candidates');
}

export async function fetchDashboardData(year: number): Promise<DashboardPayload> {
  const response = await fetch(buildApiUrl(`/api/dashboard?year=${encodeURIComponent(String(year))}`), {
    credentials: 'include',
  });
  return parseJsonResponse<DashboardPayload>(response, 'Load dashboard');
}

export async function fetchHealthData(): Promise<HealthPayload> {
  const response = await fetch(buildApiUrl('/api/health'), {
    credentials: 'include',
  });
  return parseJsonResponse<HealthPayload>(response, 'Load health');
}

export async function fetchVersionInfo(): Promise<VersionPayload> {
  const response = await fetch(buildApiUrl('/api/version'), {
    credentials: 'include',
  });
  return parseJsonResponse<VersionPayload>(response, 'Load version');
}

export interface NavCountsPayload {
  ok: boolean;
  pipeline_to_date: number | null;
  invoices_to_follow: number | null;
  data_quality_high: number | null;
}

/** Sidebar badges; every count is null until its source screen is cached. */
export async function fetchNavCounts(): Promise<NavCountsPayload> {
  const response = await fetch(buildApiUrl('/api/nav-counts'), {
    credentials: 'include',
  });
  return parseJsonResponse<NavCountsPayload>(response, 'Load navigation counts');
}

export async function fetchAuthSession(): Promise<AuthSessionPayload> {
  const response = await fetch(buildApiUrl('/api/auth/session'), {
    credentials: 'include',
  });
  return parseJsonResponse<AuthSessionPayload>(response, 'Load auth session');
}

export async function exchangeExternalAuthToken(accessToken: string, remember = false): Promise<AuthSessionPayload> {
  const response = await fetch(buildApiUrl('/api/auth/exchange'), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ access_token: accessToken, remember }),
  });
  return parseJsonResponse<AuthSessionPayload>(response, 'Exchange external auth token');
}

export async function fetchFinanceOpsData(year: number): Promise<FinanceOpsPayload> {
  const response = await fetch(buildApiUrl(`/api/finance-cockpit?year=${encodeURIComponent(String(year))}`), {
    credentials: 'include',
  });
  return parseJsonResponse<FinanceOpsPayload>(response, 'Load finance ops');
}

export async function fetchDataQualityData(year: number): Promise<DataQualityPayload> {
  const response = await fetch(buildApiUrl(`/api/data-quality?year=${encodeURIComponent(String(year))}`), {
    credentials: 'include',
  });
  return parseJsonResponse<DataQualityPayload>(response, 'Load data quality');
}

export async function fetchRevenueReconciliation(year: number): Promise<RevenueReconciliationPayload> {
  const response = await fetch(buildApiUrl(`/api/revenue-reconciliation?year=${encodeURIComponent(String(year))}`), {
    credentials: 'include',
  });
  return parseJsonResponse<RevenueReconciliationPayload>(response, 'Load revenue reconciliation');
}

export async function fetchIntegrationsStatus(): Promise<IntegrationsPayload> {
  const response = await fetch(buildApiUrl('/api/integrations'), {
    credentials: 'include',
  });
  return parseJsonResponse<IntegrationsPayload>(response, 'Load integrations');
}

export async function fetchIdentityLinkCandidates(
  year: number,
  options?: { suggestionsOnly?: boolean; limit?: number },
): Promise<IdentityLinkCandidatesPayload> {
  const params = new URLSearchParams({
    year: String(year),
    suggestions_only: String(options?.suggestionsOnly ?? false),
    limit: String(options?.limit ?? 100),
  });
  const response = await fetch(buildApiUrl(`/api/integrations/links/candidates?${params.toString()}`), {
    credentials: 'include',
  });
  return parseJsonResponse<IdentityLinkCandidatesPayload>(response, 'Load identity link candidates');
}

/** Options of THIS client's HubSpot or Qonto dropdown, suggestions first (read-only). */
export async function fetchClientIdentityOptions(
  connector: ClientIdentityConnector,
  clientId: string,
  q = '',
): Promise<ClientIdentityOptionsPayload> {
  const params = new URLSearchParams({ connector, client_id: clientId });
  if (q.trim()) params.set('q', q.trim());
  const response = await fetch(buildApiUrl(`/api/clients/identity-options?${params.toString()}`), {
    credentials: 'include',
  });
  return parseJsonResponse<ClientIdentityOptionsPayload>(response, 'Load client identity options');
}

/**
 * Longest wait for « Créer dans HubSpot »: above the server's HubSpot calls at
 * worst (portal check 2 × 8 s, duplicate check 20 s, creation 10 s), with room
 * left for the Fibery link that follows.
 */
export const HUBSPOT_COMPANY_CREATE_TIMEOUT_MS = 90_000;

/** The creation request got no answer: the company may exist in HubSpot now. */
export class HubspotCompanyUnconfirmedError extends Error {
  constructor() {
    super('HubSpot n’a pas répondu : la société a peut-être été créée. Cherchez-la avec « Rechercher dans HubSpot » avant de réessayer.');
    this.name = 'HubspotCompanyUnconfirmedError';
  }
}

/**
 * Create the client's company in HubSpot, then link it (ADR 0004). Probable
 * duplicates or an incomplete duplicate check (409, nothing created), a
 * creation whose link failed (409, `created`) and a pending link (202) come
 * back as payloads for the screen to show; any other refusal throws its
 * French message. No answer at all (timeout, network) throws
 * `HubspotCompanyUnconfirmedError`: the company may have been created.
 */
export async function createClientHubspotCompany(
  clientId: string,
  payload: CreateClientHubspotCompanyRequest,
): Promise<CreateClientHubspotCompanyResponse> {
  const csrf = await getCsrfToken();
  let response: Response;
  try {
    response = await fetch(buildApiUrl(`/api/clients/${encodeURIComponent(clientId)}/hubspot-company`), {
      method: 'POST',
      credentials: 'include',
      signal: AbortSignal.timeout(HUBSPOT_COMPANY_CREATE_TIMEOUT_MS),
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': csrf,
      },
      body: JSON.stringify(payload),
    });
  } catch {
    // TimeoutError, AbortError or a network failure: the request may have
    // reached the server, never say « rien créé ».
    throw new HubspotCompanyUnconfirmedError();
  }
  if (response.redirected && isAuthRedirect(response)) {
    return redirectToLogin();
  }
  let result: CreateClientHubspotCompanyResponse;
  try {
    result = (await response.json()) as CreateClientHubspotCompanyResponse;
  } catch {
    // An answer cut while read (timeout), or a gateway page instead of the
    // server's answer (502/504): the creation may have happened.
    if (response.ok || response.status >= 500) throw new HubspotCompanyUnconfirmedError();
    result = { ok: false, error: '' };
  }
  if (response.status === 202) return result;
  if (response.status === 409 && (Array.isArray(result.duplicates) || result.created)) return result;
  if (!response.ok || result.ok === false) {
    throw new Error(result.error || 'La société n’a pas pu être créée dans HubSpot.');
  }
  return result;
}

export async function fetchIdentityLinks(): Promise<IdentityLinksPayload> {
  const response = await fetch(buildApiUrl('/api/integrations/links'), {
    credentials: 'include',
  });
  return parseJsonResponse<IdentityLinksPayload>(response, 'Load identity links');
}

export async function confirmIdentityLink(
  payload: ConfirmIdentityLinkRequest,
): Promise<ConfirmIdentityLinkResponse> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl('/api/integrations/links'), {
    method: 'POST',
    credentials: 'include',
    signal: AbortSignal.timeout(30000),
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf,
    },
    body: JSON.stringify(payload),
  });
  return parseJsonResponse<ConfirmIdentityLinkResponse>(response, 'Confirm identity link');
}

export async function unlinkIdentityLink(
  payload: UnlinkIdentityLinkRequest,
): Promise<UnlinkIdentityLinkResponse> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl('/api/integrations/links'), {
    method: 'DELETE',
    credentials: 'include',
    signal: AbortSignal.timeout(30000),
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf,
    },
    body: JSON.stringify(payload),
  });
  return parseJsonResponse<UnlinkIdentityLinkResponse>(response, 'Unlink identity link');
}

export async function confirmSuggestedIdentityLinks(
  year: number,
): Promise<ConfirmSuggestedIdentityLinksPayload> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl('/api/integrations/links/confirm-suggested'), {
    method: 'POST',
    credentials: 'include',
    signal: AbortSignal.timeout(30000),
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf,
    },
    body: JSON.stringify({ year }),
  });
  return parseJsonResponse<ConfirmSuggestedIdentityLinksPayload>(response, 'Confirm suggested identity links');
}

export async function reconcileIdentityLinks(): Promise<ReconcileIdentityLinksPayload> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl('/api/integrations/links/reconcile'), {
    method: 'POST',
    credentials: 'include',
    signal: AbortSignal.timeout(30000),
    headers: {
      'X-CSRF-Token': csrf,
    },
  });
  return parseJsonResponse<ReconcileIdentityLinksPayload>(response, 'Reconcile identity links');
}

export async function abandonPendingIdentityLink(
  payload: UnlinkIdentityLinkRequest,
): Promise<AbandonPendingIdentityLinkPayload> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl('/api/integrations/links/pending/abandon'), {
    method: 'POST',
    credentials: 'include',
    signal: AbortSignal.timeout(30000),
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf,
    },
    body: JSON.stringify(payload),
  });
  return parseJsonResponse<AbandonPendingIdentityLinkPayload>(response, 'Abandon pending identity link');
}

export async function fetchNotificationsStatus(includeAi = true): Promise<NotificationsPayload> {
  const suffix = includeAi ? '?include_ai=1' : '';
  const response = await fetch(buildApiUrl(`/api/notifications${suffix}`), {
    credentials: 'include',
  });
  return parseJsonResponse<NotificationsPayload>(response, 'Load notifications');
}

export async function testIntegration(key: string): Promise<IntegrationTestResponse> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl(`/api/integrations/${encodeURIComponent(key)}/test`), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'X-CSRF-Token': csrf,
    },
  });
  return parseJsonResponse<IntegrationTestResponse>(response, 'Test integration');
}

export async function runIntegrationSync(
  key: string,
  options?: { year?: number; apply?: boolean },
): Promise<IntegrationSyncResponse> {
  const csrf = await getCsrfToken();
  const params = new URLSearchParams();
  if (options?.year) params.set('year', String(options.year));
  const suffix = params.toString() ? `?${params.toString()}` : '';
  const response = await fetch(buildApiUrl(`/api/integrations/${encodeURIComponent(key)}/sync${suffix}`), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf,
    },
    body: JSON.stringify({ apply: options?.apply ?? false }),
  });
  return parseJsonResponse<IntegrationSyncResponse>(response, 'Run integration sync');
}

export async function uploadReferenceWorkbook(
  file: File,
  year: number,
): Promise<ReferenceUploadResponse> {
  const csrf = await getCsrfToken();
  const body = new FormData();
  body.append('file', file);
  body.append('year', String(year));
  const response = await fetch(buildApiUrl('/api/data-trust/reference-upload'), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'X-CSRF-Token': csrf,
    },
    body,
  });
  return parseJsonResponse<ReferenceUploadResponse>(response, 'Upload reference workbook');
}

export async function fetchClient360(clientId: string, year: number): Promise<Client360Payload> {
  const response = await fetch(
    buildApiUrl(`/api/client-360?id=${encodeURIComponent(clientId)}&year=${encodeURIComponent(String(year))}`),
    {
      credentials: 'include',
    },
  );
  return parseJsonResponse<Client360Payload>(response, 'Load client 360');
}

export async function fetchAiInsightsData(year: number): Promise<AiInsightsPayload> {
  const response = await fetch(buildApiUrl(`/api/ai-insights?year=${encodeURIComponent(String(year))}`), {
    credentials: 'include',
  });
  return parseJsonResponse<AiInsightsPayload>(response, 'Load AI insights');
}

export async function fetchAiActionPlan(year: number): Promise<AiActionPlanPayload> {
  const response = await fetch(buildApiUrl(`/api/ai-actions/plan?year=${encodeURIComponent(String(year))}`), {
    credentials: 'include',
  });
  return parseJsonResponse<AiActionPlanPayload>(response, 'Load AI action plan');
}

export async function executeAiAction(
  action: AiActionItem,
  options?: { year?: number; dryRun?: boolean; schemaMode?: string },
): Promise<AiActionExecuteResponse> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl('/api/ai-actions/execute'), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf,
    },
    body: JSON.stringify({
      action: { id: action.id },
      year: options?.year,
      dry_run: options?.dryRun,
      schema_mode: options?.schemaMode,
    }),
  });
  return parseJsonResponse<AiActionExecuteResponse>(response, 'Execute AI action');
}

export async function markAllNotificationsRead(): Promise<MarkAllReadResponse> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl('/api/notifications/mark-all-read'), {
    method: 'POST',
    credentials: 'include',
    headers: {
      'X-CSRF-Token': csrf,
    },
  });
  return parseJsonResponse<MarkAllReadResponse>(response, 'Mark notifications read');
}

export async function postAction(payload: ActionPayload): Promise<ActionResponse> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl('/api/action'), {
    method: 'POST',
    credentials: 'include',
    signal: AbortSignal.timeout(45000),
    headers: {
      'Content-Type': 'application/json',
      'X-CSRF-Token': csrf,
    },
    body: JSON.stringify(payload),
  });
  const result = await parseJsonResponse<ActionResponse>(response, 'Action');
  if (result.ok === false) {
    throw new Error(result.error || 'Action refusée par le serveur.');
  }
  return result;
}

export interface ProductionSettingsPayload {
  ok: boolean;
  production_hours_per_day?: number | null;
  /** EUR HT per produced day behind the economic margin; null when not configured. */
  internal_day_cost?: number | null;
  internal_day_cost_source?: InternalDayCostSource | null;
  error?: string | null;
}

export async function fetchProductionSettings(): Promise<ProductionSettingsPayload> {
  const response = await fetch(buildApiUrl('/api/settings/production'), {
    method: 'GET',
    credentials: 'include',
  });
  return parseJsonResponse<ProductionSettingsPayload>(response, 'Load production settings');
}

export async function saveProductionSettings(hoursPerDay: number): Promise<ProductionSettingsPayload> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl('/api/settings/production'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify({ hours_per_day: hoursPerDay }),
  });
  return parseJsonResponse<ProductionSettingsPayload>(response, 'Save production settings');
}

/**
 * Internal day cost (EUR HT per produced day) behind the economic margin.
 * Reserved to `manage_settings` (admin); stored in Fibery Business Settings,
 * where it takes precedence over the server environment fallback.
 */
export async function saveInternalDayCost(internalDayCost: number): Promise<InternalDayCostSavePayload> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl('/api/settings/internal-day-cost'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify({ internal_day_cost: internalDayCost }),
  });
  const result = await parseJsonResponse<InternalDayCostSavePayload>(response, 'Save internal day cost');
  if (result.ok === false) {
    throw new Error(result.error || 'Coût interne refusé par le serveur.');
  }
  return result;
}

export async function uploadIntervenantPhoto(entityId: string, file: File): Promise<{ ok: boolean; url?: string; error?: string }> {
  const csrf = await getCsrfToken();
  const body = new FormData();
  body.append('file', file);
  const response = await fetch(buildApiUrl(`/api/intervenants/${encodeURIComponent(entityId)}/photo`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'X-CSRF-Token': csrf },
    body,
  });
  return parseJsonResponse(response, 'Envoyer la photo');
}

export async function deleteIntervenantPhoto(entityId: string): Promise<{ ok: boolean; error?: string }> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl(`/api/intervenants/${encodeURIComponent(entityId)}/photo`), {
    method: 'DELETE',
    credentials: 'include',
    headers: { 'X-CSRF-Token': csrf },
  });
  return parseJsonResponse(response, 'Supprimer la photo');
}

export function intervenantPhotoUrl(entityId: string, photoRef?: unknown): string | undefined {
  const ref = String(photoRef || '').trim();
  if (!entityId || !ref) return undefined;
  return buildApiUrl(`/api/intervenants/${encodeURIComponent(entityId)}/photo?v=${encodeURIComponent(ref).slice(0, 48)}`);
}

export async function fetchUserContext(): Promise<UserContext> {
  const response = await fetch(buildApiUrl('/api/user-context'), {
    method: 'GET',
    credentials: 'include',
  });
  return parseJsonResponse<UserContext>(response, 'Load user context');
}

export async function fetchAiChat(query: string, year: number): Promise<AiChatPayload> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl('/api/ai-chat'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify({ q: query, year, schema_mode: 'v3' }),
  });
  return parseJsonResponse<AiChatPayload>(response, 'AI Chat');
}

export async function fetchQontoBalance(): Promise<QontoBalancePayload> {
  const response = await fetch(buildApiUrl('/api/qonto/balance'), { credentials: 'include' });
  return parseJsonResponse<QontoBalancePayload>(response, 'Load Qonto balance');
}

export async function fetchWeeklyBrief(year: number): Promise<WeeklyBriefPayload> {
  const response = await fetch(buildApiUrl(`/api/ai-weekly-brief?year=${encodeURIComponent(String(year))}`), {
    credentials: 'include',
  });
  return parseJsonResponse<WeeklyBriefPayload>(response, 'Weekly brief');
}

export async function fetchMyWeek(year: number, weekStart?: string): Promise<MyWeekPayload> {
  const params = new URLSearchParams({ year: String(year) });
  if (weekStart) params.set('week_start', weekStart);
  const response = await fetch(buildApiUrl(`/api/my-week?${params.toString()}`), {
    credentials: 'include',
  });
  return parseJsonResponse<MyWeekPayload>(response, 'My week');
}

export async function fetchAiSearch(query: string, year: number): Promise<AiSearchPayload> {
  const response = await fetch(buildApiUrl(`/api/ai-search?q=${encodeURIComponent(query)}&year=${encodeURIComponent(String(year))}`), {
    credentials: 'include',
  });
  return parseJsonResponse<AiSearchPayload>(response, 'AI Search');
}

export async function fetchQontoReconciliation(year: number): Promise<ReconciliationPayload> {
  const response = await fetch(buildApiUrl(`/api/qonto/reconcile?year=${encodeURIComponent(String(year))}`), {
    credentials: 'include',
  });
  return parseJsonResponse<ReconciliationPayload>(response, 'Load reconciliation');
}

export interface AccountantPackInvoice {
  id: string;
  number: string;
  client_name: string;
  status: string;
  status_label: string;
  issue_date: string;
  paid_at: string;
  amount_ht: number;
  amount_ttc: number;
  amount_paid: number;
  currency: string;
  /** Same-origin logo route of the client (read-only, display only). */
  client_logo_url?: string;
}

export interface AccountantPackExpense {
  label: string;
  amount: number;
}

export interface AccountantPackSettings {
  recipient: string;
  cc?: string;
  personal_expenses: AccountantPackExpense[];
}

export interface AccountantPackCreditNote {
  id: string;
  number: string;
  client_name: string;
  issue_date: string;
  amount_ht: number;
  amount_ttc: number;
  currency: string;
  attachment_id: string;
  /** Same-origin logo route of the client (read-only, display only). */
  client_logo_url?: string;
}

export interface AccountantPackPayload {
  ok: boolean;
  month: string;
  month_label: string;
  invoices_issued: AccountantPackInvoice[];
  invoices_paid: AccountantPackInvoice[];
  credit_notes: AccountantPackCreditNote[];
  credit_notes_error: string;
  totals: {
    issued_ht: number;
    issued_ttc: number;
    paid_amount: number;
    credit_notes_ttc: number;
    credit_notes_ht: number;
    personal_expenses: number;
  };
  settings: AccountantPackSettings;
  source?: Record<string, unknown>;
}

export interface AccountantPackSendResponse {
  ok: boolean;
  error?: string;
  sent_to?: string;
  sent_cc?: string;
  subject?: string;
  invoices_issued?: number;
  invoices_paid?: number;
  credit_notes?: number;
  attachments?: number;
  pdf_failures?: { id: string; number: string; error: string }[];
}

export async function fetchAccountantPack(month: string): Promise<AccountantPackPayload> {
  const response = await fetch(buildApiUrl(`/api/accountant-pack?month=${encodeURIComponent(month)}`), {
    credentials: 'include',
  });
  return parseJsonResponse<AccountantPackPayload>(response, 'Load accountant pack');
}

export async function saveAccountantPackSettings(
  payload: Partial<AccountantPackSettings>,
): Promise<{ ok: boolean; settings: AccountantPackSettings }> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl('/api/accountant-pack/settings'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify(payload),
  });
  return parseJsonResponse<{ ok: boolean; settings: AccountantPackSettings }>(response, 'Save accountant settings');
}

export interface AccountantPackSendRequest {
  month: string;
  to: string;
  cc?: string;
  message?: string;
  subject?: string;
  body_text?: string;
  personal_expenses: AccountantPackExpense[];
  issued_ids: string[];
  paid_ids: string[];
  credit_note_ids: string[];
  attach_pdfs: boolean;
}

export async function sendAccountantPack(payload: AccountantPackSendRequest): Promise<AccountantPackSendResponse> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl('/api/accountant-pack/send'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify(payload),
  });
  return parseJsonResponse<AccountantPackSendResponse>(response, 'Send accountant pack');
}

export interface DealTransformResolution {
  fibery_client_id: string;
  fibery_client_options: { id: string; label: string }[];
  company_link_needed: boolean;
  company_less: boolean;
  qonto_client_id: string;
  qonto_client_options: { id: string; name: string; email?: string }[];
  qonto_clients_error: string;
  /** Ranked matches with their evidence; absent when a link already exists. */
  fibery_client_suggestions?: { id: string; label: string; score: number; reason: string }[];
  suggested_fibery_client_id?: string;
  qonto_client_suggestions?: { id: string; label: string; score: number; reason: string }[];
  suggested_qonto_client_id?: string;
}

export interface DealTransformMissionOption {
  id: string;
  label: string;
  client_name: string;
  code: string;
}

export interface DealTransformState {
  ok: boolean;
  deal: {
    id: string;
    name: string;
    company: string;
    company_external_ids: string[];
    amount: number;
    amount_available: boolean;
    currency: string;
    is_won: boolean;
    is_verbal_agreement?: boolean;
    close_date: string;
    stage: string;
    source_url: string;
  };
  already: {
    status: string;
    mission_fibery_id: string;
    invoice_external_id: string;
    invoice_url: string;
    mode: string;
    steps: { step: string; status: string; detail: string }[];
    resumable?: boolean;
    repair_available?: boolean;
    transform_state?: string;
  } | null;
  transform_state?: {
    state: 'linked_live' | 'linked_missing_recoverable' | 'transformable' | 'in_progress' | 'repair_required' | string;
    mission_fibery_id?: string;
    journal_status?: string;
    link_status?: string;
    recoverable?: boolean;
    reason?: string;
  };
  resolution: DealTransformResolution;
  mission_candidates: DealTransformMissionOption[];
  mission_options: DealTransformMissionOption[];
  proposal: {
    mission: { name: string; code: string; date_debut: string; montant_ht: number };
    invoice: {
      amount_ht: number;
      vat_rate: string;
      issue_date: string;
      due_date: string;
      purchase_order: string;
      title: string;
    };
  };
  invoice_write_enabled: boolean;
  invoice_blockers: string[];
  existing_invoices_same_client: {
    id: string;
    number: string;
    status: string;
    purchase_order: string;
    amount_ttc: number;
  }[];
}

/** One unit-of-work line entered in the transform drawer (jours or heures). */
export interface DealTransformPricingLine {
  label: string;
  billing_uo: 'JH' | 'H' | 'FF';
  qty: number;
  unit_price_ht: number;
  period_start?: string;
  period_end?: string;
}

export interface DealTransformExecuteRequest {
  deal_id: string;
  mode: 'create' | 'link';
  approved_deal: { amount: number; currency: string; company_external_id?: string };
  fibery_client_id?: string;
  create_client_name?: string;
  existing_mission_id?: string;
  mission?: { name: string; code: string; date_debut: string; delivery_start?: string; delivery_end?: string };
  create_invoice: boolean;
  invoice?: {
    amount_ht: number;
    vat_rate: string;
    issue_date: string;
    due_date: string;
    purchase_order: string;
    title: string;
  };
  qonto_client_id?: string;
  force_duplicate?: boolean;
  /** New mission only. Absent: one flat-fee line at the deal amount. */
  pricing_lines?: DealTransformPricingLine[];
}

export interface DealTransformExecuteResponse {
  ok: boolean;
  error?: string;
  correlation_id?: string;
  mission_fibery_id?: string;
  fibery_client_id?: string;
  invoice?: { invoice_id: string; invoice_url: string; organization_slug: string };
  steps?: { step: string; status: string; detail: string }[];
}

export interface HubspotCompanyOption {
  id: string;
  name?: string;
  label?: string;
  domain?: string;
  reason?: string;
}

export interface HubspotCompaniesPayload {
  ok: boolean;
  companies: HubspotCompanyOption[];
  suggestions: HubspotCompanyOption[];
  write_enabled: boolean;
  error?: string;
}

/** HubSpot companies to associate with a deal: search + suggestions (ADR 0003, read). */
export async function fetchHubspotCompanies(dealId: string, query: string): Promise<HubspotCompaniesPayload> {
  const params = new URLSearchParams({ deal_id: dealId, q: query });
  const response = await fetch(buildApiUrl(`/api/hubspot/companies?${params.toString()}`), { credentials: 'include' });
  return parseJsonResponse<HubspotCompaniesPayload>(response, 'Search HubSpot companies');
}

/** Write the association deal → company in HubSpot (ADR 0003). */
export async function associateHubspotCompany(dealId: string, companyId: string): Promise<{ ok: boolean; company?: HubspotCompanyOption; error?: string }> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl(`/api/hubspot/deals/${encodeURIComponent(dealId)}/company`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify({ company_id: companyId }),
  });
  const payload = await response.json().catch(() => ({ ok: false, error: 'Réponse illisible du serveur.' }));
  if (!response.ok || payload.ok === false) throw new Error(payload.error || 'Association refusée.');
  return payload;
}

export async function fetchDealTransformState(dealId: string): Promise<DealTransformState> {
  const response = await fetch(buildApiUrl(`/api/deal-transform?deal_id=${encodeURIComponent(dealId)}`), {
    credentials: 'include',
  });
  return parseJsonResponse<DealTransformState>(response, 'Load deal transform');
}

export async function executeDealTransform(payload: DealTransformExecuteRequest): Promise<DealTransformExecuteResponse> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl('/api/deal-transform/execute'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify(payload),
  });
  return parseJsonResponse<DealTransformExecuteResponse>(response, 'Deal transform');
}

export interface DealTransformUnlinkResponse {
  ok: boolean;
  deal_id?: string;
  mission_fibery_id?: string;
  link_removed?: boolean;
  error?: string;
}

/** Undo « Relier à une mission existante »: the deal becomes transformable again. */
export async function unlinkDealTransform(dealId: string, missionFiberyId: string): Promise<DealTransformUnlinkResponse> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl('/api/deal-transform/unlink'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify({ deal_id: dealId, mission_fibery_id: missionFiberyId }),
  });
  return parseJsonResponse<DealTransformUnlinkResponse>(response, 'Unlink deal transform');
}

export interface DealTransformReconcileResponse {
  ok: boolean;
  applied: boolean;
  already_reconciled?: boolean;
  state_before?: string;
  state_after?: string;
  mission_fibery_id?: string;
  preserved?: Record<string, unknown>;
  error?: string;
}

export async function reconcileDealTransform(dealId: string, apply: boolean): Promise<DealTransformReconcileResponse> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl('/api/deal-transform/reconcile'), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify({ deal_id: dealId, apply }),
  });
  return parseJsonResponse<DealTransformReconcileResponse>(response, apply ? 'Repair deal transform' : 'Preview deal transform repair');
}

/* ── Invoicing planned from the mission (Pascal, 05/10/2026 — ADR 0005) ── */

export type MissionBillingStateCode = 'to_plan' | 'ready' | 'draft' | 'stale' | 'sent' | 'invoiced' | 'no_lines';

export interface MissionBillingLine {
  line_id: string;
  label: string;
  billing_uo: string;
  sold_qty: number;
  planned_qty: number;
  quantity: number;
  unit_price_ht: number;
  amount_ht: number;
  remaining: number;
  remaining_unit: string;
}

export interface MissionBillingState {
  ok: boolean;
  mission_id: string;
  mission_name: string;
  state: MissionBillingStateCode;
  lines: MissionBillingLine[];
  planned_total_ht: number;
  sold_total_ht: number;
  /** Linked Qonto invoices (drafts and cancelled aside). */
  already_billed_ht: number;
  fully_planned: boolean;
  to_plan: { label: string; remaining: number; unit: string }[];
  planned_date: string;
  planned_date_is_default: boolean;
  draft: { invoice_id: string; amount_ht: number; created_at: string; qonto_status: string; number: string } | null;
  write_enabled: boolean;
  qonto_client_id: string;
  qonto_client_options: { id: string; name: string }[];
  qonto_clients_error: string;
}

export interface MissionBillingDraftResult {
  ok: boolean;
  action?: 'create' | 'update';
  invoice_id?: string;
  invoice_url?: string;
  amount_ht?: number;
  organization_slug?: string;
  error?: string;
}

export async function fetchMissionBilling(missionId: string, year: number): Promise<MissionBillingState> {
  const response = await fetch(buildApiUrl(`/api/missions/${encodeURIComponent(missionId)}/billing?year=${year}`), {
    credentials: 'include',
  });
  return parseJsonResponse<MissionBillingState>(response, 'Load mission billing');
}

export async function setMissionBillingDate(missionId: string, date: string): Promise<{ ok: boolean; planned_date: string }> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl(`/api/missions/${encodeURIComponent(missionId)}/billing/date`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify({ date }),
  });
  return parseJsonResponse<{ ok: boolean; planned_date: string }>(response, 'Set mission billing date');
}

export async function saveMissionBillingDraft(
  missionId: string,
  payload: { qonto_client_id: string; vat_rate: string; year: number },
): Promise<MissionBillingDraftResult> {
  const csrf = await getCsrfToken();
  const response = await fetch(buildApiUrl(`/api/missions/${encodeURIComponent(missionId)}/billing/draft`), {
    method: 'POST',
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', 'X-CSRF-Token': csrf },
    body: JSON.stringify(payload),
  });
  return parseJsonResponse<MissionBillingDraftResult>(response, 'Save mission invoice draft');
}

/* ── Absences planned month by month per intervenant (Pascal, 05/10/2026) ── */

export interface AbsencePlanLine {
  line_id: string;
  resource_id: string;
  resource_name: string;
  type: string;
  name: string;
  months: Record<string, number>;
}

export interface AbsencePlanPayload {
  ok: boolean;
  lines: AbsencePlanLine[];
  resources: { id: string; name: string }[];
  types: string[];
  can_edit: boolean;
}

export async function fetchAbsencePlan(): Promise<AbsencePlanPayload> {
  const response = await fetch(buildApiUrl('/api/absence-plan'), { credentials: 'include' });
  return parseJsonResponse<AbsencePlanPayload>(response, 'Load absence plan');
}

export function saveAbsenceLine(fields: {
  line_id?: string;
  resource_id: string;
  type: string;
  name: string;
  months?: Record<string, number>;
}): Promise<ActionResponse> {
  return postAction({ action: 'save_absence_line', entity_type: 'calendar_events', fields });
}

export function deleteAbsenceLine(lineId: string): Promise<ActionResponse> {
  return postAction({ action: 'delete_absence_line', entity_type: 'calendar_events', fields: { line_id: lineId } });
}
